import { useContext } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import ErrorBoundary from '@/components/error-boundary';

import { NUM_DISPLAY_RECOMMENDATIONS, ccpKeyToProductId } from '../common/constants';
import { NavProductRecommendationsContext } from '../context/nav-product-recommendations-context';

import { NewProductsContainer } from './new-products-container';
import { ProductRecommendationsContainer } from './product-recommendations-container';
import { SkeletonGrid } from './skeleton-grid';

interface GlobalNavProductRecommendationsImplementationProps {
	isMobile: boolean;
}

const GlobalNavProductRecommendationsImplementation = ({
	isMobile,
}: GlobalNavProductRecommendationsImplementationProps) => {
	const { recommendations, isRecommendationLoading } = useContext(NavProductRecommendationsContext);

	const recommendedProductIds = recommendations
		.map((ccpId) => ccpKeyToProductId[ccpId])
		.filter(Boolean);

	return (
		<div
			css={css({
				display: 'flex',
				flexDirection: 'column',
				gap: token('space.150'),
				minHeight: '448px',

				...(!isMobile && {
					gap: token('space.400'),
					paddingBlockStart: token('space.500'),
					paddingBlockEnd: token('space.500'),
					paddingInlineStart: token('space.400'),
					paddingInlineEnd: token('space.500'),
				}),
			})}
		>
			{isRecommendationLoading ? (
				<SkeletonGrid numProducts={NUM_DISPLAY_RECOMMENDATIONS} isMobile={isMobile} />
			) : (
				<ProductRecommendationsContainer
					recommendedProductIds={recommendedProductIds}
					isMobile={isMobile}
				/>
			)}

			{isRecommendationLoading ? (
				<SkeletonGrid numProducts={3} isMobile={isMobile} />
			) : (
				<NewProductsContainer excludedProducts={recommendedProductIds} isMobile={isMobile} />
			)}
		</div>
	);
};

export interface GlobalNavProductRecommendationsProps {
	isMobile?: boolean;
}

export const GlobalNavProductRecommendations = ({
	isMobile = false,
}: GlobalNavProductRecommendationsProps) => {
	const { onGlobalNavProductRecommendationsFailed } = useContext(NavProductRecommendationsContext);

	return (
		<ErrorBoundary fallback={null} onError={onGlobalNavProductRecommendationsFailed}>
			<GlobalNavProductRecommendationsImplementation isMobile={isMobile} />
		</ErrorBoundary>
	);
};
